import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Btn } from "@/components/site/kit";
import { useSite } from "@/context/SiteContext";
import { api, cycleLabel, errorMessage, inr } from "@/lib/api";
import { fbEvent } from "@/lib/consent";

const STATES = ["Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"];

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export const totals = (price, s) => {
  const rate = s?.gst_rate ?? 18;
  if (s?.gst_mode === "inclusive") return { base: price / (1 + rate / 100), gst: price - price / (1 + rate / 100), total: price, rate };
  return { base: price, gst: (price * rate) / 100, total: price * (1 + rate / 100), rate };
};

const EMPTY = { name: "", email: "", phone: "", gst_number: "", billing_state: "", agree_terms: false, website: "" };

export const CheckoutDialog = ({ item, open, onOpenChange }) => {
  const { settings } = useSite();
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open && item) fbEvent("InitiateCheckout", { content_name: item.name, value: item.price, currency: "INR" });
  }, [open, item]);

  if (!item) return null;
  const t = totals(item.price, settings);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (f.name.trim().length < 2) e.name = "Enter your full name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Enter a valid email";
    if (!/^\+?[0-9\s-]{10,15}$/.test(f.phone.trim())) e.phone = "Enter a valid 10-digit mobile number";
    if (f.gst_number && !/^[0-9]{2}[A-Z0-9]{13}$/i.test(f.gst_number.trim())) e.gst_number = "Enter a valid 15-character GSTIN";
    if (!f.billing_state) e.billing_state = "Select your state";
    if (!f.agree_terms) e.agree_terms = "Please accept the policies to continue";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const pay = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/create-order", { ...f, item_type: item.type, item_id: item.id });
      if (!(await loadRazorpay())) throw new Error("Could not load Razorpay. Check your connection.");
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.razorpay_order_id,
        name: data.name, description: data.description, prefill: data.prefill, notes: { order_id: data.order_id },
        theme: { color: "#2A2D7C" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", resp);
            navigate(`/payment/success?order=${data.order_id}`);
          } catch (err) {
            navigate(`/payment/failed?order=${data.order_id}&reason=${encodeURIComponent(errorMessage(err))}`);
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      rzp.on("payment.failed", (resp) => {
        const reason = resp?.error?.description || "Payment failed";
        api.post("/payments/failed", { order_id: data.order_id, reason }).catch(() => {});
        rzp.close?.();
        navigate(`/payment/failed?order=${data.order_id}&reason=${encodeURIComponent(reason)}`);
      });
      onOpenChange(false);
      rzp.open();
    } catch (err) {
      toast.error(errorMessage(err));
      setLoading(false);
    }
  };

  const err = (k) => errors[k] && <p className="mt-1 text-xs font-medium text-red-600" data-testid={`checkout-${k}-error`}>{errors[k]}</p>;
  const input = (k, label, props) => (
    <div>
      <label className="label-base" htmlFor={`co-${k}`}>{label}</label>
      <input id={`co-${k}`} className="input-base" value={f[k]} onChange={set(k)} data-testid={`checkout-${k}-input`} {...props} />
      {err(k)}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setLoading(false); }}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-2xl bg-white sm:max-w-lg" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-bold text-navy">Checkout</DialogTitle>
          <DialogDescription className="text-body">{item.name} · {inr(item.price)} {cycleLabel(item.billing_cycle)}</DialogDescription>
        </DialogHeader>
        <form onSubmit={pay} noValidate className="space-y-3.5" data-testid="checkout-form">
          {input("name", "Full name *", { autoComplete: "name" })}
          <div className="grid gap-3.5 sm:grid-cols-2">
            {input("email", "Email *", { type: "email", autoComplete: "email" })}
            {input("phone", "Mobile *", { type: "tel", autoComplete: "tel" })}
          </div>
          <div className="grid gap-3.5 sm:grid-cols-2">
            {input("gst_number", "GSTIN (optional)", { placeholder: "06ABCDE1234F1Z5" })}
            <div>
              <label className="label-base" htmlFor="co-state">Billing state *</label>
              <select id="co-state" className="input-base" value={f.billing_state} onChange={set("billing_state")} data-testid="checkout-billing-state-select">
                <option value="">Select state</option>
                {STATES.map((s) => <option key={s}>{s}</option>)}
              </select>
              {err("billing_state")}
            </div>
          </div>
          <div className="rounded-xl bg-page p-4 text-sm" data-testid="checkout-summary">
            <div className="flex justify-between"><span>Price</span><span>{inr(t.base)}</span></div>
            <div className="flex justify-between"><span>GST ({t.rate}%)</span><span>{inr(t.gst)}</span></div>
            <div className="mt-2 flex justify-between border-t border-line pt-2 font-bold text-navy"><span>Total payable</span><span data-testid="checkout-total">{inr(t.total)}</span></div>
          </div>
          <label className="flex items-start gap-2.5 text-sm text-body">
            <Checkbox checked={f.agree_terms} onCheckedChange={(v) => setF((p) => ({ ...p, agree_terms: !!v }))} className="mt-0.5" data-testid="checkout-agree-checkbox" />
            <span>I agree to the <Link to="/terms-and-conditions" target="_blank" className="font-semibold text-ocean hover:underline">Terms</Link>, <Link to="/privacy-policy" target="_blank" className="font-semibold text-ocean hover:underline">Privacy Policy</Link> and <Link to="/refund-policy" target="_blank" className="font-semibold text-ocean hover:underline">Refund Policy</Link>.</span>
          </label>
          {err("agree_terms")}
          <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="absolute left-[-9999px] h-0 w-0 opacity-0" aria-hidden="true" />
          <Btn type="submit" disabled={loading} className="w-full" arrow={!loading} testId="checkout-pay-btn">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Processing..." : `Pay ${inr(t.total)}`}
          </Btn>
          <p className="flex items-center justify-center gap-1.5 text-xs text-muted2"><Lock className="h-3.5 w-3.5" />Secured by Razorpay · Cards, UPI, Netbanking & Wallets</p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
